import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";

import Etiqueta from "../Etiqueta";
import Indicador from "../Indicador";
import {
    ROTULOS_ACAO,
    formatarDataHora,
    formatarNumero,
    formatarPrimeiroDeficit,
    rotuloConfianca,
} from "../shared";

export default function DecisaoAbastecimento({
    acoes,
    indicadores,
    onAbrirComposicao,
    onAbrirConfianca,
    onAtualizarHistorico,
    previsoes,
    textoHorizontePcp,
}) {
    return (
        <>
            <div className="p-d-flex p-jc-between p-ai-start p-mb-3">
                <div>
                    <div style={{ color: '#263238', fontSize: 20, fontWeight: 700 }}>
                        O que precisa de atenção agora
                    </div>
                    <div style={{ color: '#607d8b', marginTop: 4 }}>
                        Horizonte analisado: {textoHorizontePcp}. A situação considera o tecido disponível, a engomagem e as roladas planejadas.
                    </div>
                </div>
                <Button
                    className="p-button-sm p-button-secondary"
                    icon="pi pi-refresh"
                    label="Atualizar histórico"
                    onClick={onAtualizarHistorico}
                />
            </div>

            <div className="p-d-flex p-flex-wrap" style={{ gap: 12 }}>
                <Indicador
                    descricao="Artigos que ainda terão falta considerando todo o plano."
                    situacao="critico"
                    titulo="Ação imediata"
                    valor={indicadores.criticos}
                />
                <Indicador
                    descricao="A falta é evitada pela engomagem ou por uma rolada planejada."
                    situacao="atencao"
                    titulo="Cobertos pelo plano"
                    valor={indicadores.cobertosPeloPlano}
                />
                <Indicador
                    descricao="Artigos com cobertura adequada no horizonte analisado."
                    situacao="normal"
                    titulo="Situação estável"
                    valor={indicadores.estaveis}
                />
                <Indicador
                    descricao="Previsões que precisam de validação por falta de histórico."
                    situacao="neutro"
                    titulo="Sem histórico"
                    valor={indicadores.semHistorico}
                />
            </div>

            <div style={{ color: '#263238', fontSize: 17, fontWeight: 700, margin: '24px 0 8px' }}>
                Ações recomendadas
            </div>
            <DataTable
                emptyMessage="Nenhuma ação necessária para o horizonte atual."
                responsiveLayout="scroll"
                size="small"
                value={acoes}
            >
                <Column header="Prioridade" body={(rowData) => {
                    const configuracao = {
                        1: { texto: 'Ação imediata', situacao: 'critico' },
                        2: { texto: 'Confirmar hoje', situacao: 'atencao' },
                        3: { texto: 'Manter', situacao: 'normal' },
                        4: { texto: 'Oportunidade', situacao: 'neutro' },
                    }[rowData.prioridade];

                    return <Etiqueta {...configuracao} />
                }} />
                <Column header="Artigo" field="cod_artigo" sortable />
                <Column header="O que fazer" body={(rowData) => (
                    <strong>{ROTULOS_ACAO[rowData.tipo] || 'Revisar planejamento'}</strong>
                )} />
                <Column header="Planejado para" body={(rowData) => formatarDataHora(
                    rowData.data_disponibilidade_planejada || rowData.data_inicio_planejada
                )} />
                <Column header="Prazo recomendado" body={(rowData) => formatarDataHora(
                    rowData.data_disponibilidade_sugerida || rowData.data_limite
                )} />
                <Column header="Cobertura considerada" body={(rowData) => {
                    const previsao = rowData.previsao;

                    if (!previsao) {
                        return '-';
                    }

                    return `${formatarNumero(previsao.pcp.metros_faltantes)} m de tecido exigem ${previsao.pcp.rolos_necessarios ?? '-'} rolo(s); há ${previsao.estoque_tecelagem} na tecelagem, ${previsao.quantidade_engomagem} na engomagem e ${previsao.rolos_planejados} planejados`;
                }} />
                <Column header="Por quê" body={(rowData) => (
                    <div>
                        <div>{rowData.descricao}</div>
                        {rowData.regra_aplicada && (
                            <div style={{ color: '#607d8b', fontSize: 12, marginTop: 4 }}>
                                Regra aplicada: {rowData.regra_aplicada}
                            </div>
                        )}
                    </div>
                )} />
                <Column header="Confiança" body={(rowData) => (
                    <Button
                        className="p-button-sm p-button-text"
                        icon="pi pi-info-circle"
                        label={rotuloConfianca(rowData.confianca)}
                        onClick={() => onAbrirConfianca({
                            cod_artigo: rowData.cod_artigo,
                            confianca: rowData.confianca,
                            quantidade_roladas_historico: rowData.previsao?.quantidade_roladas_historico ?? 0,
                        })}
                    />
                )} />
            </DataTable>

            <div style={{ color: '#263238', fontSize: 17, fontWeight: 700, margin: '24px 0 8px' }}>
                Situação por artigo
            </div>
            <DataTable
                emptyMessage="Não existem artigos na programação atual."
                responsiveLayout="scroll"
                size="small"
                value={previsoes}
            >
                <Column header="Situação" body={(rowData) => (
                    <Etiqueta texto={rowData.situacaoLabel} situacao={rowData.situacao} />
                )} />
                <Column header="Artigo" field="cod_artigo" sortable />
                <Column header="Demanda de tecido" body={(rowData) => `${formatarNumero(rowData.pcp.metros_demandados)} m`} />
                <Column header="Tecido disponível e em produção" body={(rowData) => `${formatarNumero(rowData.pcp.metros_disponiveis)} m`} />
                <Column header="Déficit de tecido" body={(rowData) => `${formatarNumero(rowData.pcp.metros_faltantes)} m`} />
                <Column header="Rolos necessários" body={(rowData) => (
                    rowData.demanda_horizonte === null ? 'Sem histórico' : `${rowData.demanda_horizonte} rolo(s)`
                )} />
                <Column header="Oferta de urdume" body={(rowData) => (
                    `${rowData.estoque_tecelagem} na tecelagem + ${rowData.quantidade_engomagem} engomagem + ${rowData.rolos_planejados} planejados`
                )} />
                <Column header="Primeiro déficit de tecido" body={(rowData) => formatarPrimeiroDeficit(
                    rowData.pcp.primeira_falta
                )} />
                <Column header="Resultado após o plano" body={(rowData) => {
                    const saldo = rowData.cenario_potencial.saldo_final;
                    return saldo >= 0
                        ? `${saldo} rolo(s) de margem`
                        : `${Math.abs(saldo)} rolo(s) em falta`;
                }} />
                <Column header="Qualidade da previsão" body={(rowData) => (
                    <Button
                        className="p-button-sm p-button-text"
                        icon="pi pi-info-circle"
                        label={rotuloConfianca(rowData.confianca)}
                        onClick={() => onAbrirConfianca({
                            cod_artigo: rowData.cod_artigo,
                            confianca: rowData.confianca,
                            quantidade_roladas_historico: rowData.quantidade_roladas_historico,
                        })}
                    />
                )} />
                <Column header="Conferência" body={(rowData) => (
                    <Button
                        className="p-button-sm p-button-text"
                        icon="pi pi-search"
                        label="Ver composição"
                        onClick={() => onAbrirComposicao(rowData)}
                    />
                )} />
            </DataTable>
        </>
    );
}
